import { UnitAccountViewModel } from '@ail/customers-api'
import { skipToken } from '@reduxjs/toolkit/query'
import { useMemo, useState } from 'react'
import DataGrid, { Column } from 'react-data-grid'
import { useTranslation } from 'react-i18next'
import { FrontendSettings } from '../../../../helpers/FrontendSettings'
import { useFilteredCustomers } from '../../../../hooks/useFilteredCustomers'
import { useUnitsByCustomer } from '../../../../hooks/useUnitsByCustomer'
import { useGetUserQuery } from '../../../../redux/api/customerApi'
import { FrontendSettingsKeys } from '../../../../types/enums'
import './ShowUsersUnitsFunctional.scss'
import { Loader } from '../../../common/Loader/Loader'

interface UnitRow {
  id: string
  customerName: string
  unitName: string
}

interface Props {
  selectedUserId: string
  onClose: () => void
}

export default function ShowUsersUnitsFunctional({ selectedUserId, onClose }: Props) {
  const { t } = useTranslation('common')
  const [searchString, setSearchString] = useState('')

  const tenantId = FrontendSettings.get(FrontendSettingsKeys.preselectedTenantId) || ''
  const { currentData: user } = useGetUserQuery(
    selectedUserId && tenantId ? { tenantId, userId: selectedUserId } : skipToken,
    { refetchOnMountOrArgChange: true }
  )
  //! forced refetch until newUserEdit is ported to RTK and tag invalidation works
  //! without it after unit updates, user has stale data due to RTK not refetching post update
  const unitsByCustomer = useUnitsByCustomer()
  const { customersMap } = useFilteredCustomers()

  const cols = useMemo<Column<UnitRow>[]>(
    () => [
      { key: 'customerName', name: t('CUSTOMER') },
      {
        key: 'unitName',
        name: t('UNIT_NAME'),
        renderCell: ({ row }) => <>{row.unitName || t('NO_COST_CENTER')}</>,
      },
    ],
    [t]
  )

  const allRows = useMemo<UnitRow[]>(() => {
    if (!user) return []
    const customerUnits: UnitRow[] = (user.customers ?? [])
      .filter((c) => c.tenantId === tenantId)
      .flatMap((c) => {
        const units: UnitAccountViewModel[] = unitsByCustomer[c.id!] ?? []
        return units.map((u) => ({ id: u.id, customerName: c.name ?? '', unitName: u.name }))
      })
    const specificUnits: UnitRow[] = (user.units ?? []).map((u) => {
      const c = u.customerId ? customersMap[`$${u.customerId.toUpperCase()}`] : undefined

      return { id: u.id, customerName: c?.name ?? '', unitName: u.name }
    })
    const allAssignedUnits = [...customerUnits, ...specificUnits].sort(
      (a, b) => +!a.unitName - +!b.unitName || (a.unitName ?? '').localeCompare(b.unitName ?? '')
    )

    return allAssignedUnits
  }, [user, tenantId, unitsByCustomer, customersMap])

  const visibleRows = useMemo(
    () =>
      searchString ? allRows.filter((r) => r.unitName.toLowerCase().includes(searchString.toLowerCase())) : allRows,
    [allRows, searchString]
  )
  return (
    <>
      <div className="showUsersUnits">
        <Loader blocking={false}>
          <a className="showUsersUnits__close" onClick={onClose}>
            <i className="icon-close" />
          </a>
          <div className="showUsersUnits__header">
            <div className="showUsersUnits__title">
              <i className="icon-assign" />
              {t('UNITS')}
            </div>
            <div className="showUsersUnits__search">
              <input
                autoFocus
                type="text"
                autoComplete="off"
                value={searchString}
                placeholder={t('FILTER_UNIT_BY_KEYWORD')}
                onChange={(e) => setSearchString(e.currentTarget.value)}
              />
              <button title={t('CLEAR')} onClick={() => setSearchString('')}>
                <span className="icon-clear" />
              </button>
              <i className="icon-search" />
            </div>
          </div>
          <div className="showUsersUnits__grid">
            <DataGrid className="rdg-light" columns={cols} rows={visibleRows} rowHeight={45} />
          </div>
        </Loader>
      </div>
      <div className="blur" />
    </>
  )
}
